import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Icon from '../components/Icon'
import { TextField } from '../components/fields'
import {
  BULK_ORDER_UNKNOWN_MATERIAL_GROUP,
  filterCatalog,
  getBulkOrderFilterOptions,
  groupCatalogByBulkOrderMaterial,
  matchesBulkOrderMaterialFilter,
  selectCatalogUi,
  setBulkOrderCatalogUi,
} from '../../common/bulkOrder'
import BulkOrderScrollContainer from './BulkOrderScrollContainer'
import {
  Sidebar,
  SidebarHeader,
  FilterSection,
  FilterLabel,
  FilterPills,
  FilterPill,
  CatalogListViewport,
  GroupHeader,
  GroupHeaderLabel,
  GroupHeaderCount,
  GroupChevron,
  ProductGroup,
  ProductRow,
  ProductName,
  CoatingBadge,
  EmptyState,
} from './BulkOrderCatalogSidebar.styles'

const BulkOrderCatalogSidebar = ({
  catalog = [],
  selectedLensId = null,
  onSelectLens = () => {},
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { searchText, materialKey, coatingKey, expandedGroups } = useSelector(selectCatalogUi)

  const { materialGroupKeys, coatingKeys } = useMemo(
    () => getBulkOrderFilterOptions(catalog),
    [catalog],
  )

  const filteredCatalog = useMemo(
    () =>
      filterCatalog(catalog, {
        searchText,
        materialKey,
        coatingKey,
        matchesMaterial: matchesBulkOrderMaterialFilter,
      }),
    [catalog, searchText, materialKey, coatingKey],
  )

  const groups = useMemo(() => groupCatalogByBulkOrderMaterial(filteredCatalog), [filteredCatalog])

  const toggleGroup = key => {
    dispatch(
      setBulkOrderCatalogUi({
        expandedGroups: {
          ...expandedGroups,
          [key]: !expandedGroups[key],
        },
      }),
    )
  }

  const handleMaterialFilter = key => {
    if (key) {
      dispatch(setBulkOrderCatalogUi({ materialKey: key, expandedGroups: { [key]: true } }))
      return
    }
    dispatch(setBulkOrderCatalogUi({ materialKey: null }))
  }

  const getGroupLabel = key =>
    key === BULK_ORDER_UNKNOWN_MATERIAL_GROUP ? t('other') : t(key)

  return (
    <Sidebar>
      <SidebarHeader>
        <TextField
          name="bulk-order-catalog-search"
          value={searchText}
          placeholder={t('Search products...')}
          onChange={({ value }) => dispatch(setBulkOrderCatalogUi({ searchText: value }))}
          icon="IosClose"
          onIconClick={() => dispatch(setBulkOrderCatalogUi({ searchText: '' }))}
        />
      </SidebarHeader>

      {materialGroupKeys.length > 0 && (
        <FilterSection>
          <FilterLabel>{t('material')}</FilterLabel>
          <FilterPills>
            <FilterPill
              $isActive={!materialKey}
              aria-pressed={!materialKey}
              onClick={() => handleMaterialFilter(null)}
              type="button"
            >
              {t('All')}
            </FilterPill>
            {materialGroupKeys.map(key => (
              <FilterPill
                key={key}
                $isActive={materialKey === key}
                aria-pressed={materialKey === key}
                onClick={() => handleMaterialFilter(key)}
                type="button"
              >
                {t(key)}
              </FilterPill>
            ))}
          </FilterPills>
        </FilterSection>
      )}

      {coatingKeys.length > 0 && (
        <FilterSection>
          <FilterLabel>{t('coating')}</FilterLabel>
          <FilterPills>
            <FilterPill
              $isActive={!coatingKey}
              aria-pressed={!coatingKey}
              onClick={() => dispatch(setBulkOrderCatalogUi({ coatingKey: null }))}
              type="button"
            >
              {t('All')}
            </FilterPill>
            {coatingKeys.map(key => (
              <FilterPill
                key={key}
                $isActive={coatingKey === key}
                aria-pressed={coatingKey === key}
                onClick={() => dispatch(setBulkOrderCatalogUi({ coatingKey: key }))}
                type="button"
              >
                {key}
              </FilterPill>
            ))}
          </FilterPills>
        </FilterSection>
      )}

      <CatalogListViewport>
        <BulkOrderScrollContainer>
          {groups.length === 0 && <EmptyState>{t('No products match your filters')}</EmptyState>}

          {groups.map(group => {
            const isExpanded = !!expandedGroups[group.key]
            return (
              <div key={group.key}>
                <GroupHeader
                  type="button"
                  $isExpanded={isExpanded}
                  onClick={() => toggleGroup(group.key)}
                >
                  <GroupChevron $isExpanded={isExpanded}>
                    <Icon name="IosArrowDown" size={16} withoutTooltip />
                  </GroupChevron>
                  <GroupHeaderLabel>
                    {getGroupLabel(group.key)}
                    <GroupHeaderCount>({group.items.length})</GroupHeaderCount>
                  </GroupHeaderLabel>
                </GroupHeader>

                {isExpanded && (
                  <ProductGroup>
                    {group.items.map(item => {
                      const isSelected = selectedLensId === item._id
                      return (
                      <ProductRow
                        key={item._id}
                        type="button"
                        $isSelected={isSelected}
                        onClick={() => onSelectLens(item._id)}
                      >
                        <ProductName title={item.name} $isSelected={isSelected}>
                          {item.name}
                        </ProductName>
                        <CoatingBadge $isSelected={isSelected}>{item.filterCoating}</CoatingBadge>
                      </ProductRow>
                      )
                    })}
                  </ProductGroup>
                )}
              </div>
            )
          })}
        </BulkOrderScrollContainer>
      </CatalogListViewport>
    </Sidebar>
  )
}

BulkOrderCatalogSidebar.propTypes = {
  catalog: PropTypes.array,
  selectedLensId: PropTypes.string,
  onSelectLens: PropTypes.func,
}

export default BulkOrderCatalogSidebar
